import { h, Component } from 'preact';
import { joinClasses } from 'utils/utils';
import { clamp } from 'utils/math';
import { isVideoFile } from 'utils/assets';
import { showModal } from 'utils/modal';

import Button from 'components/button/button';
import WithTooltip from 'components/tooltip/withTooltip';

import s from 'components/media/mediaViewer.sss';

const CLICK_SLOP = 8;

export default class MediaViewer extends Component {
	constructor(props) {
		super(props);

		this.state = {
			scale: 1,
			tx: 0, ty: 0,
		};

		this.close = this.close.bind(this);
		this.onClick = this.onClick.bind(this);
		this.onWheel = this.onWheel.bind(this);
		this.onGrab = this.onGrab.bind(this);
		this.onDrag = this.onDrag.bind(this);
		this.onRelease = this.onRelease.bind(this);
		this.resetView = this.resetView.bind(this);
	}

	componentDidMount() {
		document.addEventListener('pointermove', this.onDrag);
		document.addEventListener('pointerup', this.onRelease);
	}
	componentWillUnmount() {
		document.removeEventListener('pointermove', this.onDrag);
		document.removeEventListener('pointerup', this.onRelease);
	}

	close() {
		this.base.close();
	}

	getTranslateBounds() {
		const asset = this.base.querySelector(`.${s.asset}`);
		if (!asset) {
			return { xMin: -Infinity, xMax: Infinity, yMin: -Infinity, yMax: Infinity };
		}
		const w = asset.clientWidth || 0;
		const h = asset.clientHeight || 0;
		return {
			xMin: -w / 2, xMax: w / 2,
			yMin: -h / 2, yMax: h / 2,
		};
	}

	onClick(e) {
		if (this.preventNextClick) {
			this.preventNextClick = false;
			return e.preventDefault();
		}
		if (e.target === this.base) {
			return this.close();
		}
	}

	onWheel(e) {
		e.preventDefault();

		const state = this.state;

		const factor = Math.exp(-e.deltaY / 500);
		let scale = clamp(state.scale * factor, .1, 10);
		const actualFactor = scale / state.scale;

		const bounds = this.getTranslateBounds();
		const x = e.clientX - window.innerWidth / 2;
		const y = e.clientY - window.innerHeight / 2;
		const tx = clamp((state.tx - x) * actualFactor + x, bounds.xMin * scale, bounds.xMax * scale);
		const ty = clamp((state.ty - y) * actualFactor + y, bounds.yMin * scale, bounds.yMax * scale);

		this.setState({ scale, tx, ty });
	}

	onGrab(e) {
		if (this.grabbed || e.button !== 0) return;
		e.preventDefault();
		this.base.classList.add(s.dragging);
		this.grabbed = {
			x: e.clientX,
			y: e.clientY,
			tx: this.state.tx,
			ty: this.state.ty,
		};
	}
	onDrag(e) {
		if (!this.grabbed) return;
		e.preventDefault();
		const dx = e.clientX - this.grabbed.x;
		const dy = e.clientY - this.grabbed.y;
		if (Math.abs(dx) > CLICK_SLOP || Math.abs(dy) > CLICK_SLOP) {
			this.preventNextClick = true;
			this.grabbed.pastSlop = true;
		}
		if (!this.grabbed.pastSlop) return;
		const bounds = this.getTranslateBounds();
		const scale = this.state.scale;
		const tx = clamp(this.grabbed.tx + dx, bounds.xMin * scale, bounds.xMax * scale);
		const ty = clamp(this.grabbed.ty + dy, bounds.yMin * scale, bounds.yMax * scale);
		this.setState({ tx, ty });
	}
	onRelease(e) {
		if (!this.grabbed) return;
		e.preventDefault();
		this.grabbed = false;
		this.base.classList.remove(s.dragging);
	}

	resetView() {
		this.setState({ scale: 1, tx: 0, ty: 0 });
	}

	getAssetStyle() {
		const { scale, tx, ty } = this.state;
		return { scale: `${scale}`, translate: `${tx}px ${ty}px` };
	}
	isViewTransformed() {
		const { scale, tx, ty } = this.state;
		return scale !== 1 || tx !== 0 || ty !== 0;
	}

	renderAsset() {
		const item = this.props.item;
		if (isVideoFile(item.s3_url)) {
			return <video class={s.asset} src={item.s3_url} style={this.getAssetStyle()} controls />;
		}
		return <img class={s.asset} src={item.s3_url} style={this.getAssetStyle()} alt="" />;
	}

	render(props) {
		const item = props.item;
		return (
			<dialog
				class={joinClasses(s.mediaViewer, this.grabbed && s.dragging, this.isViewTransformed() && s.viewTransformed)}
				onClick={this.onClick}
				onWheel={this.onWheel}
				onPointerDown={this.onGrab}
			>
				<div class={s.info}>
					<Button dark icon="cancel" onClick={this.close} />
					<div class={s.filename}>{item.name}</div>
				</div>
				<Button class={s.download} dark iconAlt="download" href={item.s3_url} download={item.name} target="_blank">
					Download
				</Button>
				{this.renderAsset()}
				<WithTooltip class={s.resetView} tooltip="Reset view" direction="top" align="center" interactive>
					<Button dark icon="revert" onClick={this.resetView} />
				</WithTooltip>
			</dialog>
		);
	}
}

export async function showMediaViewer(item) {
	return showModal(MediaViewer, { item });
}